import { fetchViatorActivitiesByDestination } from "lib/api/algolia/activities";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-cool-inview";

import ViatorProductCard from "@/components/card/viator/ViatorProductCard";
import styles from "./ActivitiesSection.module.scss";

export default function ActivitiesSection({ destinationSlug, activities }) {
  const [viatorProducts, setViatorProducts] = useState([]);
  const [activityFallbackViatorProducts, setActivityFallbackViatorProducts] =
    useState([]);
  const [
    destinationFallbackViatorProducts,
    setDestinationFallbackViatorProducts,
  ] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingFallbackProducts, setLoadingFallbackProducts] = useState(true);
  const [lastLevelFetched, setLastLevelFetched] = useState(0);

  const router = useRouter();

  const { inView, observe } = useInView({
    unobserveOnEnter: true,
  });

  useEffect(() => {
    setViatorProducts([]);
    setActivityFallbackViatorProducts([]);
    setDestinationFallbackViatorProducts([]);
    observe();
  }, [router.asPath]);

  const { list_item: activitiesList } = activities;
  const activityIds = activitiesList.map((activity) => activity.id);

  function findMissingArrayItems(baseArray, comparisonArray) {
    return baseArray.filter(
      (item) =>
        !comparisonArray
          .map((item) => item.toString())
          .includes(item.toString())
    );
  }

  function getViatorProductActivityIds(products) {
    const viatorProductActivityIds =
      products?.hits?.length > 0
        ? products?.hits.map(
            (product) => product.wherenext_destination_activity_id
          )
        : [];

    return viatorProductActivityIds;
  }

  useEffect(
    () => {
      if (!inView) return;

      async function fetchProductsByActivity() {
        if (viatorProducts?.hits?.length > 0) return;
        // Inital fetch of products by activity id
        setLoadingProducts(true);

        const viatorProductsResponse = await fetchViatorActivitiesByDestination(
          {
            destinationSlug,
            fallback: false,
          }
        );

        setViatorProducts(viatorProductsResponse);
        setLoadingProducts(false);
        return viatorProductsResponse;
      }

      async function fetchFallbackProductsByActivity(missingActivityIds = []) {
        if (activityFallbackViatorProducts?.hits?.length > 0) return;
        setLoadingFallbackProducts(true);

        const activityFallbackViatorProductsResponse =
          await fetchViatorActivitiesByDestination({
            destinationSlug,
            fallback: true,
            ids: missingActivityIds,
          });

        setActivityFallbackViatorProducts(
          activityFallbackViatorProductsResponse
        );
        setLoadingFallbackProducts(false);

        return activityFallbackViatorProductsResponse;
      }

      async function fetchFallbackProductsByDestination() {
        if (destinationFallbackViatorProducts?.hits?.length > 0) return;

        setLoadingFallbackProducts(true);

        const destinationFallbackViatorProductsResponse =
          await fetchViatorActivitiesByDestination({
            destinationSlug,
            fallback: true,
          });

        setDestinationFallbackViatorProducts(
          destinationFallbackViatorProductsResponse
        );
        setLoadingFallbackProducts(false);

        return destinationFallbackViatorProductsResponse;
      }

      (async () => {
        if (viatorProducts?.hits?.length > 0) return;

        const viatorProductsResponse = await fetchProductsByActivity();

        // Check for activities missing from the initial fetch.
        // If they're missing, fetch destination-region fallback activities
        const viatorProductActivityIds = getViatorProductActivityIds(
          viatorProductsResponse
        );

        const missingActivityIds = findMissingArrayItems(
          activityIds,
          viatorProductActivityIds
        );

        if (missingActivityIds.length === 0) {
          return;
        }

        const activityFallbackViatorProductsResponse =
          await fetchFallbackProductsByActivity(missingActivityIds);

        // Check for activities missing from the fallback fetch.
        const viatorFallbackProductActivityIds = getViatorProductActivityIds(
          activityFallbackViatorProductsResponse
        );

        const missingFallbackActivityIds = findMissingArrayItems(
          missingActivityIds,
          viatorFallbackProductActivityIds
        );

        if (missingFallbackActivityIds.length === 0) {
          return;
        }

        // If there are still activities missing, fetch destination fallback activities
        const destinationFallbackViatorProductsResponse =
          await fetchFallbackProductsByDestination();
      })();
    },
    // [inView, destinationSlug, activitiesList, router.asPath, activityIds],
    [inView, destinationSlug, router.asPath]
  );

  let currentFallbackIndex = 0;

  const activityItems = useMemo(
    () =>
      activitiesList.map((activity, index) => {
        if (
          !viatorProducts?.hits?.length > 0 &&
          !activityFallbackViatorProducts?.hits?.length > 0 &&
          !destinationFallbackViatorProducts?.hits?.length > 0
        ) {
          return (
            <ActivityItem
              activity={activity}
              index={index}
              key={`${activity.title}-${index}`}
              loadingProducts={false}
            />
          );
        }

        let isFallback = false;
        let associatedProducts = [];

        const productsByActivityId = viatorProducts?.hits?.filter(
          (product) =>
            parseInt(product.wherenext_destination_activity_id) ===
            parseInt(activity.id)
        );

        let fallbackProductsByActivityId = [];

        if (!!activityFallbackViatorProducts?.hits?.length > 0) {
          isFallback = true;
          fallbackProductsByActivityId =
            activityFallbackViatorProducts?.hits.filter(
              (product) =>
                parseInt(product.wherenext_destination_activity_id) ===
                parseInt(activity.id)
            );
        }

        associatedProducts = [
          ...(productsByActivityId ?? []),
          ...(fallbackProductsByActivityId ?? []),
        ];

        if (
          associatedProducts?.length === 0 &&
          destinationFallbackViatorProducts?.hits?.length > 0
        ) {
          const filterWords = [
            "airport",
            "transfer",
            "shuttle",
            "bus",
            "taxi",
            "car",
            "van",
            "minivan",
          ];

          const currentProduct =
            destinationFallbackViatorProducts?.hits?.filter((product) => {
              return !product.title
                .split(" ")
                .some((word) => filterWords.includes(word.toLowerCase()));
            })[currentFallbackIndex];

          associatedProducts = [currentProduct];

          currentFallbackIndex++;
        }

        return (
          <ActivityItem
            activity={activity}
            index={index}
            key={`${activity.title}-${index}`}
            associatedProducts={associatedProducts ?? []}
            loadingProducts={
              isFallback ? loadingFallbackProducts : loadingProducts
            }
          />
        );
      }),
    [
      activitiesList,
      activityFallbackViatorProducts?.hits,
      currentFallbackIndex,
      destinationFallbackViatorProducts?.hits,
      loadingFallbackProducts,
      loadingProducts,
      viatorProducts?.hits,
    ]
  );

  return (
    <ul className={`${styles.activityList}`} ref={observe}>
      {!!activityItems && activityItems}
    </ul>
  );
}

function ActivityItem({
  activity,
  index,
  associatedProducts,
  loadingProducts,
}) {
  return (
    <li className={`${styles.activityItem}`}>
      <div className={`${styles.info}`}>
        <h3 className={`${styles.itemTitle}`}>
          {index + 1}. {activity.title}
        </h3>
        <p className={`${styles.itemDescription}`}>{activity.description}</p>
      </div>

      {associatedProducts && (
        <div className={`${styles.associatedProducts}`}>
          <ViatorProductCard
            isLoaded={!loadingProducts}
            product={associatedProducts[0]}
            horizontal={true}
          />
        </div>
      )}
    </li>
  );
}
